import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Scheduler tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function SchedulerAdminTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  return (
    <div className="space-y-4" data-testid="scheduler-tab">
      <h2 className="text-2xl font-playfair font-bold text-foreground mb-4 flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-green-500" /> Scheduler Service Moderation
                  </h2>

                  {/* Pending Services */}
                  <div className="wm-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                      Pending Approval ({pendingServices.length})
                    </h3>
              
                    {pendingServices.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No services pending approval</p>
                    ) : (
                      <div className="space-y-4">
                        {pendingServices.map((service) => (
                          <div key={service.id} className="bg-card p-4 rounded-lg border border-border">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-foreground">{service.name}</h4>
                                <p className="text-sm text-primary mb-1">{service.category}</p>
                                <p className="text-sm text-muted-foreground mb-2">{service.description}</p>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <span>Duration: {service.duration} min</span>
                                  <span>Price: ${service.price}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                  By: {service.owner_name} ({service.owner_email})
                                </p>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <Button
                                  onClick={() => handleApproveService(service.id)}
                                  className="bg-green-600 hover:bg-green-700"
                                  size="sm"
                                  data-testid="approve-service-btn"
                                >
                                  <Check className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button
                                  onClick={() => handleFlagService(service.id)}
                                  variant="destructive"
                                  size="sm"
                                  data-testid="flag-service-btn"
                                >
                                  <Flag className="w-4 h-4 mr-1" /> Flag
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Flagged Services */}
                  <div className="wm-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Flag className="w-5 h-5 text-red-500" />
                      Flagged Services ({flaggedServices.length})
                    </h3>
              
                    {flaggedServices.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No flagged services</p>
                    ) : (
                      <div className="space-y-4">
                        {flaggedServices.map((service) => (
                          <div key={service.id} className="bg-red-500/10 p-4 rounded-lg border border-red-500/30">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-foreground">{service.name}</h4>
                                <p className="text-sm text-primary mb-1">{service.category}</p>
                                <p className="text-sm text-muted-foreground mb-2">{service.description}</p>
                                {service.flag_reason && (
                                  <p className="text-sm text-red-400 mb-2">
                                    <strong>Flag reason:</strong> {service.flag_reason}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  By: {service.owner_name} ({service.owner_email})
                                </p>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <Button
                                  onClick={() => handleApproveService(service.id)}
                                  className="bg-green-600 hover:bg-green-700"
                                  size="sm"
                                >
                                  <Check className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button
                                  onClick={() => handleDeleteService(service.id)}
                                  variant="destructive"
                                  size="sm"
                                >
                                  <X className="w-4 h-4 mr-1" /> Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
    </div>
  );
}
